import { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

const AgentContext = createContext(null);

export function AgentProvider({ children }) {
  // Current agent being created/edited
  const [currentAgent, setCurrentAgent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Knowledge files for current agent
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);

  // Available AI models from backend
  const [availableModels, setAvailableModels] = useState([]);

  // Training status
  const [trainingStatus, setTrainingStatus] = useState(null);

  // Chat history for testing
  const [chatHistory, setChatHistory] = useState([]);
  const [isClearing, setIsClearing] = useState(false);

  // WhatsApp pairing state
  const [whatsappPairing, setWhatsappPairing] = useState(null);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Step 1: Create new agent
  const createAgent = useCallback(async (data) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.createAgent(data);
      setCurrentAgent(result.agent);
      return result.agent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update agent name
  const updateAgentName = useCallback(async (data) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.updateAgentName(currentAgent.id, data);
      setCurrentAgent(result.agent);
      return result.agent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  // Step 2: Update behavior
  const updateAgentBehavior = useCallback(async (data) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.updateAgentBehavior(currentAgent.id, data);
      setCurrentAgent(result.agent);
      return result.agent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  // Step 3: Knowledge files
  const loadKnowledgeFiles = useCallback(async () => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    try {
      const result = await api.getKnowledgeFiles(currentAgent.id);
      setKnowledgeFiles(result.files || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  const uploadKnowledgeFile = useCallback(async (file) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.uploadKnowledgeFile(currentAgent.id, file);
      setKnowledgeFiles(prev => [...prev, result.file]);
      return result.file;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  const deleteKnowledgeFile = useCallback(async (fileId) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      await api.deleteKnowledgeFile(currentAgent.id, fileId);
      setKnowledgeFiles(prev => prev.filter(f => f.id !== fileId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  // Step 4: Model selection
  const loadAvailableModels = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await api.getAvailableModels();
      setAvailableModels(result.models || []);
      return result;
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateAgentModel = useCallback(async (data) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.updateAgentModel(currentAgent.id, data);
      setCurrentAgent(result.agent);
      return result.agent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  // Step 5: Features
  const updateAgentFeatures = useCallback(async (data) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.updateAgentFeatures(currentAgent.id, data);
      setCurrentAgent(result.agent);
      return result.agent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  // Step 6: Training
  const trainAgent = useCallback(async () => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    setTrainingStatus({ status: 'training' });
    try {
      const result = await api.trainAgent(currentAgent.id);
      setTrainingStatus({
        status: 'completed',
        ...result,
      });
      return result;
    } catch (err) {
      setError(err.message);
      setTrainingStatus({ status: 'error', error: err.message });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  const loadTrainingStatus = useCallback(async () => {
    if (!currentAgent?.id) return;
    try {
      const result = await api.getTrainingStatus(currentAgent.id);
      setTrainingStatus(result);
      return result;
    } catch (err) {
      setError(err.message);
    }
  }, [currentAgent?.id]);

  // Step 7: Test chat - fetch history from Firestore
  const fetchChatHistory = useCallback(async () => {
    if (!currentAgent?.id) return;
    try {
      const result = await api.getSimulatorHistory(currentAgent.id);
      if (result.messages) {
        // Convert from frontend format to context format
        const historyMessages = result.messages.map(msg => ({
          id: msg.id,
          role: msg.type === 'user' ? 'user' : 'assistant',
          content: msg.text,
          time: msg.time,
        }));
        setChatHistory(historyMessages);
      }
    } catch (err) {
      console.error('Failed to fetch chat history:', err);
    }
  }, [currentAgent?.id]);

  const sendTestMessage = useCallback(async (message) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);

    // Add user message to history optimistically
    const userMsg = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: message,
      time: new Date().toISOString(),
    };
    setChatHistory(prev => [...prev, userMsg]);

    try {
      // API now handles saving to Firestore and uses server-side history
      const result = await api.chatWithAgent(currentAgent.id, message);

      // Add AI response to history
      const aiMsg = {
        id: `msg-${Date.now()}-ai`,
        role: 'assistant',
        content: result.response,
        time: new Date().toISOString(),
      };
      setChatHistory(prev => [...prev, aiMsg]);

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  const clearChatHistory = useCallback(async () => {
    if (!currentAgent?.id) return;
    setIsClearing(true);
    try {
      await api.clearSimulatorHistory(currentAgent.id);
      setChatHistory([]);
    } catch (err) {
      console.error('Failed to clear chat history:', err);
      // Clear locally anyway
      setChatHistory([]);
    } finally {
      setIsClearing(false);
    }
  }, [currentAgent?.id]);

  // Step 8 & 9: WhatsApp pairing
  const startWhatsAppPairing = useCallback(async (phoneNumber) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.pairWhatsApp(currentAgent.id, phoneNumber);
      setWhatsappPairing({
        status: result.status,
        sessionId: result.session_id,
        qrImageUrl: result.qr_image_url,
        qrString: result.qr_string,
        expiresAt: result.expires_at,
        phoneNumber: phoneNumber,
      });
      return result;
    } catch (err) {
      setError(err.message);
      setWhatsappPairing({ status: 'error', error: err.message });
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  const checkWhatsAppStatus = useCallback(async () => {
    if (!currentAgent?.id) return;
    try {
      const result = await api.getWhatsAppStatus(currentAgent.id);

      // Determine the actual phone number to use
      // If phone_updated is true, use actual_phone (user scanned with different account)
      const actualPhone = result.phone_updated ? result.actual_phone : result.phone;

      setWhatsappPairing(prev => ({
        ...prev,
        status: result.status,
        phone: actualPhone,
        phoneUpdated: result.phone_updated || false,
      }));

      // Update current agent with WhatsApp status
      if (result.status === 'connected') {
        setCurrentAgent(prev => ({
          ...prev,
          whatsapp_status: 'connected',
          whatsapp_phone: actualPhone,
          whatsapp_phone_number: actualPhone ? `+${actualPhone}` : prev?.whatsapp_phone_number,
        }));
      }
      return result;
    } catch (err) {
      console.error('Failed to check WhatsApp status:', err);
      return { status: 'error', error: err.message };
    }
  }, [currentAgent?.id]);

  const disconnectWhatsApp = useCallback(async () => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      await api.disconnectWhatsApp(currentAgent.id);
      setWhatsappPairing(null);
      setCurrentAgent(prev => ({
        ...prev,
        whatsapp_status: 'disconnected',
      }));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  const resetWhatsAppPairing = useCallback(() => {
    setWhatsappPairing(null);
  }, []);

  const refreshQRCode = useCallback(async () => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.refreshWhatsAppQR(currentAgent.id);

      // If already connected, update state accordingly
      if (result.status === 'connected') {
        setWhatsappPairing(prev => ({
          ...prev,
          status: 'connected',
        }));
        setCurrentAgent(prev => ({
          ...prev,
          whatsapp_status: 'connected',
        }));
        return result;
      }

      // Update pairing state with new QR code
      setWhatsappPairing(prev => ({
        ...prev,
        status: result.status,
        qrImageUrl: result.qr_image_url,
        qrString: result.qr_string,
        expiresAt: result.expires_at,
      }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  // Load existing agent
  const loadAgent = useCallback(async (agentId) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.getAgent(agentId);
      setCurrentAgent(result.agent);
      setKnowledgeFiles(result.agent.knowledge_files || []);
      // Load training status if agent was trained
      if (result.agent.training_status === 'completed') {
        setTrainingStatus({ status: 'completed' });
      }
      return result.agent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Update step progress
  const updateStepProgress = useCallback(async (step) => {
    if (!currentAgent?.id) return;
    try {
      await api.updateStepProgress(currentAgent.id, step);
      setCurrentAgent(prev => ({
        ...prev,
        current_step: Math.max(step, prev?.current_step || 0)
      }));
    } catch (err) {
      console.error('Failed to update step progress:', err);
    }
  }, [currentAgent?.id]);

  // Reset context (when starting new agent creation)
  const resetAgent = useCallback(() => {
    setCurrentAgent(null);
    setKnowledgeFiles([]);
    setTrainingStatus(null);
    setChatHistory([]);
    setError(null);
  }, []);

  // Step 10: Launch agent (Go Live)
  const launchAgent = useCallback(async (data) => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.launchAgent(currentAgent.id, data);
      setCurrentAgent(result.agent);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  // Deactivate agent
  const deactivateAgent = useCallback(async () => {
    if (!currentAgent?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.deactivateAgent(currentAgent.id);
      setCurrentAgent(result.agent);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [currentAgent?.id]);

  const value = {
    // State
    currentAgent,
    knowledgeFiles,
    availableModels,
    trainingStatus,
    chatHistory,
    whatsappPairing,
    isLoading,
    isClearing,
    error,

    // Actions
    clearError,
    createAgent,
    updateAgentName,
    updateAgentBehavior,
    loadKnowledgeFiles,
    uploadKnowledgeFile,
    deleteKnowledgeFile,
    loadAvailableModels,
    updateAgentModel,
    updateAgentFeatures,
    trainAgent,
    loadTrainingStatus,
    fetchChatHistory,
    sendTestMessage,
    clearChatHistory,
    loadAgent,
    updateStepProgress,
    resetAgent,
    // WhatsApp
    startWhatsAppPairing,
    checkWhatsAppStatus,
    disconnectWhatsApp,
    resetWhatsAppPairing,
    refreshQRCode,
    // Go Live
    launchAgent,
    deactivateAgent,
  };

  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
}

export function useAgent() {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within an AgentProvider');
  }
  return context;
}